import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Server, Brain, CheckCircle, XCircle, Loader2, Eye, EyeOff, Zap, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { apiClient, ConfigStatus } from '@/utils/apiClient';
import { clsx } from 'clsx';
import { LLMProviderSettings } from './settings/LLMProviderSettings';

interface ConfigurationPageProps {
  onConfigured: () => void;
}

interface FieldState {
  value: string;
  testing: boolean;
  testResult?: 'success' | 'error';
  testMessage?: string;
  visible: boolean;
}

const initialField = (initial = ''): FieldState => ({
  value: initial,
  testing: false,
  visible: false,
});

export const ConfigurationPage: React.FC<ConfigurationPageProps> = ({ onConfigured }) => {
  const { t } = useTranslation();
  const [configStatus, setConfigStatus] = useState<ConfigStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  // Cluster form fields
  const [clusterUrl, setClusterUrl] = useState<FieldState>(initialField());
  const [clusterToken, setClusterToken] = useState<FieldState>(initialField());
  const [envToken, setEnvToken] = useState<FieldState>(initialField());

  // Load current config status
  const loadStatus = useCallback(async () => {
    try {
      const status = await apiClient.getConfigStatus();
      setConfigStatus(status);
    } catch {
      // Backend may not be ready yet
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const updateField = (
    setter: React.Dispatch<React.SetStateAction<FieldState>>,
    updates: Partial<FieldState>,
  ) => {
    setter(prev => ({ ...prev, ...updates }));
  };

  const handleTestCluster = async () => {
    // Use the entered value, or try fetching the stored one if cluster is configured
    let testUrl = clusterUrl.value;
    if (!testUrl && configStatus?.credentials.cluster_url) {
      // Cluster URL is already configured but we don't have the raw value.
      // The user must enter it again to test. This is by design — secrets
      // are never sent back from the server in plaintext.
      return;
    }
    if (!testUrl) return;

    updateField(setClusterUrl, { testing: true, testResult: undefined });
    try {
      const resp = await apiClient.testSecret('cluster_url', testUrl);
      updateField(setClusterUrl, {
        testing: false,
        testResult: resp.valid ? 'success' : 'error',
        testMessage: resp.message,
      });
    } catch {
      updateField(setClusterUrl, { testing: false, testResult: 'error', testMessage: t.configuration.testFailed });
    }
  };

  const handleSaveCluster = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      const writes: Array<{ path: string; value: string }> = [];
      if (clusterUrl.value) writes.push({ path: 'mcp/cluster_base_url', value: clusterUrl.value });
      if (clusterToken.value) writes.push({ path: 'mcp/cluster_api_token', value: clusterToken.value });
      if (envToken.value) writes.push({ path: 'mcp/environment_api_token', value: envToken.value });

      for (const w of writes) {
        await apiClient.setSecret(w.path, w.value);
      }

      // Trigger MCP reload to pick up new cluster tokens
      await apiClient.reloadAllServices();
      await new Promise(r => setTimeout(r, 2000));

      const status = await apiClient.getConfigStatus();
      setConfigStatus(status);

      // Move to step 2 automatically
      setStep(2);
    } catch (err: any) {
      setSaveError(err?.message || t.configuration.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  // Check LLM status and auto-complete if configured
  const checkAndComplete = useCallback(async () => {
    setSaveError(null);
    try {
      const status = await apiClient.getConfigStatus();
      setConfigStatus(status);
      if (status.configured) {
        onConfigured();
      } else {
        // Never fail silently: say which half is missing so the user isn't
        // stuck on a button that appears to do nothing (BUG-1.3.0_5).
        const llmOk = status.credentials.gigachat || status.credentials.ollama
          || status.credentials.openai || status.credentials.anthropic
          || status.llm_available;
        setSaveError(!llmOk
          ? t.configuration.finishBlockedLlm
          : t.configuration.finishBlockedCluster);
      }
    } catch (err: any) {
      setSaveError(err?.message || t.configuration.saveFailed);
    }
  }, [onConfigured, t]);

  const clusterConfigured = configStatus?.credentials.cluster_url
    && configStatus?.credentials.cluster_token
    && configStatus?.credentials.env_token;

  const clusterComplete = clusterConfigured || (clusterUrl.value && clusterToken.value && envToken.value);
  const canTestCluster = clusterUrl.value || configStatus?.credentials.cluster_url;

  const llmConfigured = configStatus?.credentials.gigachat
    || configStatus?.credentials.ollama
    || configStatus?.credentials.openai
    || configStatus?.credentials.anthropic
    || (configStatus?.llm_available);

  const StatusBadge: React.FC<{ configured: boolean }> = ({ configured }) => (
    <span className={clsx(
      'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full',
      configured
        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
    )}>
      {configured
        ? <><CheckCircle className="h-3 w-3" /> {t.configuration.configured}</>
        : <><XCircle className="h-3 w-3" /> {t.configuration.notConfigured}</>
      }
    </span>
  );

  const SecretInput: React.FC<{
    label: string;
    placeholder: string;
    field: FieldState;
    setField: React.Dispatch<React.SetStateAction<FieldState>>;
    configured?: boolean;
  }> = ({ label, placeholder, field, setField, configured }) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        {configured !== undefined && <StatusBadge configured={configured} />}
      </div>
      <div className="relative">
        <input
          type={field.visible ? 'text' : 'password'}
          value={field.value}
          onChange={e => updateField(setField, { value: e.target.value, testResult: undefined })}
          placeholder={configured ? t.configuration.alreadyConfigured : placeholder}
          className={clsx(
            'w-full px-3 py-2 pr-10 rounded-lg border text-sm',
            'bg-white dark:bg-gray-800',
            'text-gray-900 dark:text-white',
            'placeholder-gray-400 dark:placeholder-gray-500',
            field.testResult === 'error'
              ? 'border-red-300 dark:border-red-600'
              : field.testResult === 'success'
                ? 'border-green-300 dark:border-green-600'
                : configured
                  ? 'border-green-200 dark:border-green-800'
                  : 'border-gray-300 dark:border-gray-600',
            'focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none',
          )}
        />
        <button
          type="button"
          onClick={() => updateField(setField, { visible: !field.visible })}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          {field.visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {field.testResult === 'error' && field.testMessage && (
        <p className="text-xs text-red-500">{field.testMessage}</p>
      )}
      {field.testResult === 'success' && (
        <p className="text-xs text-green-500">{field.testMessage || t.configuration.testSuccess}</p>
      )}
    </div>
  );

  // Step indicators
  const StepIndicator: React.FC = () => (
    <div className="flex items-center justify-center gap-3 mb-6">
      <div className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
        step === 1
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          : clusterComplete
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
      )}>
        {clusterComplete && step !== 1
          ? <CheckCircle className="h-4 w-4" />
          : <Server className="h-4 w-4" />
        }
        {t.configuration.step1Label}
      </div>
      <ArrowRight className="h-4 w-4 text-gray-400" />
      <div className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
        step === 2
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          : llmConfigured
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
      )}>
        {llmConfigured && step !== 2
          ? <CheckCircle className="h-4 w-4" />
          : <Brain className="h-4 w-4" />
        }
        {t.configuration.step2Label}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Header */}
      <header className="flex-shrink-0 flex flex-col items-center pt-12 pb-6 px-4 text-center">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/40">
            <Zap className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
            Ключ-Астром <span className="text-blue-600 dark:text-blue-400">MCP</span>
          </h1>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{t.configuration.title}</h2>
        </div>
        <p className="max-w-lg text-sm text-gray-500 dark:text-gray-400">{t.configuration.subtitle}</p>
      </header>

      {/* Step Indicator */}
      <StepIndicator />

      {/* Form */}
      <main className="flex-1 max-w-xl mx-auto w-full px-4 pb-12 space-y-6">

        {/* ===== STEP 1: Cluster Connection ===== */}
        {step === 1 && (
          <>
            <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Server className="h-5 w-5 text-blue-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">{t.configuration.clusterSection}</h3>
              </div>

              {clusterConfigured && !clusterUrl.value && !clusterToken.value && !envToken.value && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {t.configuration.clusterAlreadyConfigured}
                  </p>
                </div>
              )}

              <SecretInput
                label={t.configuration.clusterUrl}
                placeholder={t.configuration.clusterUrlPlaceholder}
                field={clusterUrl}
                setField={setClusterUrl}
                configured={configStatus?.credentials.cluster_url}
              />
              <SecretInput
                label={t.configuration.clusterToken}
                placeholder={t.configuration.clusterTokenPlaceholder}
                field={clusterToken}
                setField={setClusterToken}
                configured={configStatus?.credentials.cluster_token}
              />
              <SecretInput
                label={t.configuration.envToken}
                placeholder={t.configuration.envTokenPlaceholder}
                field={envToken}
                setField={setEnvToken}
                configured={configStatus?.credentials.env_token}
              />

              <div className="flex items-center gap-3">
                <button
                  onClick={handleTestCluster}
                  disabled={!canTestCluster || clusterUrl.testing}
                  className={clsx(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium',
                    'border border-gray-300 dark:border-gray-600',
                    'text-gray-700 dark:text-gray-300',
                    'hover:bg-gray-100 dark:hover:bg-gray-700',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors',
                  )}
                >
                  {clusterUrl.testing
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.configuration.testing}</>
                    : t.configuration.testConnection
                  }
                </button>
              </div>
            </section>

            {/* Save & Continue / Skip */}
            {saveError && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>
              </div>
            )}

            <div className="flex gap-3">
              {/* Save new cluster credentials and continue */}
              {(clusterUrl.value || clusterToken.value || envToken.value) && (
                <button
                  onClick={handleSaveCluster}
                  disabled={saving}
                  className={clsx(
                    'flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-medium text-white',
                    'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-all duration-200',
                  )}
                >
                  {saving
                    ? <><Loader2 className="h-5 w-5 animate-spin" /> {t.configuration.saving}</>
                    : <>{t.configuration.saveAndContinue} <ArrowRight className="h-4 w-4" /></>
                  }
                </button>
              )}

              {/* Skip to step 2 if cluster already configured */}
              {clusterConfigured && (
                <button
                  onClick={() => setStep(2)}
                  className={clsx(
                    'flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-medium',
                    'border border-gray-300 dark:border-gray-600',
                    'text-gray-700 dark:text-gray-300',
                    'hover:bg-gray-100 dark:hover:bg-gray-700',
                    'transition-all duration-200',
                  )}
                >
                  {t.configuration.continueToLlm} <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </>
        )}

        {/* ===== STEP 2: LLM Provider ===== */}
        {step === 2 && (
          <>
            <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="h-5 w-5 text-purple-500" />
                <h3 className="font-semibold text-gray-900 dark:text-white">{t.configuration.llmSection}</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t.configuration.atLeastOneLlm}</p>

              {/* Embed the full LLM Provider Settings component.
                  onProvidersChanged: without it the mount-time configStatus goes
                  stale after Activate and Finish setup never enables (BUG-1.3.0_5). */}
              <LLMProviderSettings onProvidersChanged={loadStatus} />
            </section>

            {saveError && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className={clsx(
                  'flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-medium',
                  'border border-gray-300 dark:border-gray-600',
                  'text-gray-700 dark:text-gray-300',
                  'hover:bg-gray-100 dark:hover:bg-gray-700',
                  'transition-all duration-200',
                )}
              >
                <ArrowLeft className="h-4 w-4" /> {t.configuration.backToCluster}
              </button>

              <button
                onClick={checkAndComplete}
                disabled={!llmConfigured}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-medium text-white',
                  'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
                  'shadow-lg shadow-blue-600/25 dark:shadow-blue-500/25',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-all duration-200',
                )}
              >
                {t.configuration.finishSetup}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};
